/* Made Active delivery preview. Read-only Shopify cart + Zapiet Widget API. */
(() => {
  'use strict';
  const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const DAY_MS = 86400000;
  const dateValue = value => {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('Invalid calendar date');
    const stamp = Date.parse(value + 'T00:00:00Z');
    if (!Number.isFinite(stamp) || new Date(stamp).toISOString().slice(0, 10) !== value) throw new Error('Invalid calendar date');
    return stamp;
  };
  const iso = stamp => new Date(stamp).toISOString().slice(0, 10);
  const pretty = value => new Intl.DateTimeFormat('en-AU', {weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC'}).format(new Date(dateValue(value)));
  function arrayDate(value) {
    if (!Array.isArray(value) || value.length < 3 || !value.slice(0, 3).every(Number.isInteger)) throw new Error('Unsupported calendar rule');
    return dateValue(`${value[0]}-${String(value[1] + 1).padStart(2, '0')}-${String(value[2]).padStart(2, '0')}`);
  }
  function ruleMatches(rule, stamp) {
    if (Number.isInteger(rule) && rule >= 1 && rule <= 7) return new Date(stamp).getUTCDay() + 1 === rule;
    if (Array.isArray(rule) && (rule.length === 3 || (rule.length === 4 && rule[3] === 'inverted'))) return arrayDate(rule) === stamp;
    if (typeof rule === 'string') return dateValue(rule) === stamp;
    if (rule && typeof rule === 'object' && !Array.isArray(rule) && Object.keys(rule).every(k => ['from', 'to', 'inverted'].includes(k))) {
      const bound = v => Array.isArray(v) ? arrayDate(v) : dateValue(v);
      return stamp >= bound(rule.from) && stamp <= bound(rule.to);
    }
    throw new Error('Unsupported calendar rule');
  }
  function candidates(calendar, count = 12) {
    if (!calendar.minDate) return [];
    const start = dateValue(calendar.minDate);
    const end = calendar.maxDate ? dateValue(calendar.maxDate) : start + 365 * DAY_MS;
    if (!Array.isArray(calendar.disabled)) throw new Error('Missing calendar rules');
    const rules = calendar.disabled.slice();
    const inverted = rules[0] === true;
    if (inverted) rules.shift();
    // Validate every rule before offering any dates. Unknown formats fail closed.
    rules.forEach(rule => ruleMatches(rule, start));
    const result = [];
    for (let stamp = start; stamp <= Math.min(end, start + 365 * DAY_MS) && result.length < count; stamp += DAY_MS) {
      const normal = rules.filter(r => !(Array.isArray(r) ? r[3] === 'inverted' : r?.inverted === true));
      const exceptions = rules.filter(r => !normal.includes(r));
      let blocked = normal.some(r => ruleMatches(r, stamp));
      if (exceptions.some(r => ruleMatches(r, stamp))) blocked = false;
      if (inverted) blocked = !blocked;
      if (!blocked) result.push(iso(stamp));
    }
    return result;
  }
  function usualCutoff(date, location) {
    // Only infer the ordinary end-of-day rule when every preparation setting is
    // an explicit positive whole number of calendar days, with no time breakpoint.
    const values = DAYS.map(day => location.delivery?.[day]);
    if (values.some(v => !v || v.useBreakpoint || v.before?.format !== 'days' || !Number.isInteger(Number(v.before.value)) || Number(v.before.value) < 1)) return null;
    const delivery = dateValue(date);
    for (let offset = 1; offset <= 35; offset++) {
      const order = delivery - offset * DAY_MS;
      if (Number(values[new Date(order).getUTCDay()].before.value) <= offset) return iso(order);
    }
    return null;
  }
  function availableSlots(slots) {
    if (!Array.isArray(slots)) throw new Error('Invalid time availability');
    return slots.some(slot => slot && (!slot.limited || Number(slot.limit) > 0));
  }
  const helpers = {dateValue, iso, pretty, candidates, usualCutoff, availableSlots};
  if (typeof module !== 'undefined' && module.exports) { module.exports = helpers; return; }
  if (customElements.get('made-delivery-finder')) return;

  class DeliveryFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-postcode]');
      this.status = this.querySelector('[data-status]');
      this.result = this.querySelector('[data-result]');
      this.button = this.querySelector('button[type="submit"]');
      this.submitLabel = this.button.textContent;
      this.onSubmit = e => { e.preventDefault(); this.lookup(); };
      this.onInput = () => {
        this.cancel(); this.result.hidden = true; this.status.textContent = '';
        this.input.removeAttribute('aria-invalid');
      };
      this.onVisibility = () => {
        if (!document.hidden && this.checkedAt && Date.now() - this.checkedAt > 300000) {
          this.result.hidden = true;
          this.status.textContent = 'Please check again for the latest delivery dates.';
          this.checkedAt = 0;
        }
      };
      this.form.addEventListener('submit', this.onSubmit);
      this.input.addEventListener('input', this.onInput);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.expiryTimer = setInterval(this.onVisibility, 30000);
    }
    disconnectedCallback() {
      this.cancel();
      this.form?.removeEventListener('submit', this.onSubmit);
      this.input?.removeEventListener('input', this.onInput);
      document.removeEventListener('visibilitychange', this.onVisibility);
      clearInterval(this.expiryTimer);
      this.ready = false;
    }
    cancel() {
      this.checkedAt = 0;
      this.request?.abort();
      this.button.disabled = false;
      this.button.textContent = this.submitLabel;
      this.form.removeAttribute('aria-busy');
    }
    async json(url, options, signal) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener('abort', abort, {once: true});
      if (signal.aborted) controller.abort();
      const timer = setTimeout(abort, 15000);
      try {
        const response = await fetch(url, {...options, cache: 'no-store', signal: controller.signal});
        if (!response.ok) throw new Error('Request unavailable');
        return await response.json();
      } finally { clearTimeout(timer); signal.removeEventListener('abort', abort); }
    }
    async context(signal) {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await this.json(root + 'cart.js', {credentials: 'same-origin'}, signal);
      if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
      const items = cart.items.filter(item => item.requires_shipping && !item.gift_card);
      const handles = [...new Set(items.length ? items.map(item => item.handle) : [this.dataset.product])];
      if (handles.some(handle => !handle)) throw new Error('Choose a preview product in the theme editor');
      const products = await Promise.all(handles.map(handle => this.json(root + 'products/' + encodeURIComponent(handle) + '.js', {credentials: 'same-origin'}, signal)));
      const map = new Map(products.map((product, index) => [handles[index], product]));
      const format = (product, variant, quantity) => ({variant_id: variant.id, product_id: product.id, sku: variant.sku || '', quantity, tags: product.tags || [], vendors: [product.vendor || ''], product_types: [product.type || '']});
      let shoppingCart;
      if (items.length) {
        shoppingCart = items.map(item => {
          const product = map.get(item.handle);
          const variant = product?.variants?.find(v => v.id === item.variant_id);
          if (!variant) throw new Error('Product details unavailable');
          return {...format(product, variant, item.quantity), has_components: Boolean(item.has_components)};
        });
      } else {
        const product = products[0];
        const variant = product?.variants?.find(v => v.available && v.requires_shipping !== false);
        if (!variant) throw new Error('Preview product unavailable');
        shoppingCart = [format(product, variant, 1)];
      }
      return {shoppingCart, hasItems: Boolean(items.length)};
    }
    async api(path, body, signal) {
      const shop = this.dataset.shop;
      if (!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(shop || '')) throw new Error('Store unavailable');
      return this.json('https://api.zapiet.com/v1.0/' + path + '?shop=' + encodeURIComponent(shop), {
        method: 'POST', credentials: 'omit', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body)
      }, signal);
    }
    text(selector, value) { this.querySelector(selector).textContent = value; }
    async lookup() {
      this.cancel();
      const postcode = this.input.value.trim();
      this.result.hidden = true;
      if (!/^\d{4}$/.test(postcode)) {
        this.input.setAttribute('aria-invalid', 'true');
        this.status.textContent = 'Enter a valid 4-digit Australian postcode.';
        this.input.focus(); return;
      }
      this.input.removeAttribute('aria-invalid');
      this.input.value = postcode;
      const request = new AbortController();
      this.request = request;
      const signal = request.signal;
      this.button.disabled = true;
      this.button.textContent = 'Checking…';
      this.form.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Checking delivery to ' + postcode + '…';
      try {
        const context = await this.context(signal);
        const location = await this.api('delivery/locations', {geoSearchQuery: postcode, shoppingCart: context.shoppingCart}, signal);
        if (signal.aborted) return;
        if (location.error?.code === 'NO_LOCATIONS_FOUND') {
          this.status.textContent = context.hasItems ? 'Delivery to ' + postcode + ' is not available for the items in your cart. Please contact us if you need a hand.' : 'Sorry, we don’t currently deliver to ' + postcode + '. Please check another postcode.';
          return;
        }
        if (location.error || !Number.isInteger(Number(location.id)) || !location.delivery) throw new Error('Invalid delivery location');
        this.text('[data-result-heading]', 'Good food. Delivered to ' + postcode + '.');
        const regular = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].filter(day => location.delivery[day]?.slots?.enabled);
        const dayList = this.querySelector('[data-days]'); dayList.replaceChildren();
        regular.forEach(day => {const item = document.createElement('li'); item.textContent = day.charAt(0).toUpperCase() + day.slice(1); dayList.append(item);});
        this.querySelector('[data-regular]').hidden = !regular.length;
        this.querySelector('[data-date-card]').hidden = true;
        this.querySelector('[data-more-dates]').hidden = true;
        this.querySelector('[data-cutoff]').hidden = true;
        this.text('[data-calendar-message]', 'Finding your next delivery dates…');
        this.text('[data-note]', context.hasItems ? 'Preview based on the items currently in your cart. Choose and confirm your delivery date in the cart.' : 'A preview for our standard meals. Your final delivery options are confirmed with your order in the cart.');
        this.result.hidden = false;
        this.status.textContent = 'We deliver to ' + postcode + '. Checking available dates.';
        const cta = this.querySelector('[data-shop-link]');
        cta.href = context.hasItems ? (window.Shopify?.routes?.root || '/') + 'cart' : this.dataset.shopLink;
        cta.textContent = context.hasItems ? 'Choose your delivery date →' : this.dataset.shopLabel;
        try {
          const calendar = await this.api('delivery/locations/' + location.id + '/calendar', {shoppingCart: context.shoppingCart}, signal);
          if (calendar.error || calendar.datePickerEnabled === false || location.has_daily_limits_enabled === true) throw new Error('Calendar unavailable');
          const dates = [];
          let dateOnly = window.ZapietCachedSettings?.cached_config?.delivery_time_picker_enabled === false;
          for (const date of candidates(calendar)) {
            if (dates.length === 3 || signal.aborted) break;
            if (dateOnly) { dates.push(date); continue; }
            const slots = date === calendar.minDate && calendar.delivEnabled === false && Array.isArray(calendar.minDateSlots)
              ? calendar.minDateSlots
              : await this.api('delivery/locations/' + location.id + '/calendar/' + date, {postal_code: postcode, shoppingCart: context.shoppingCart}, signal);
            if (slots?.error === 'TIME_PICKER_NOT_ENABLED') {
              // The date-only widget uses the calendar's disabled rules; the
              // times endpoint explicitly rejects stores without time selection.
              dateOnly = true; dates.push(date);
            } else if (availableSlots(slots)) dates.push(date);
          }
          if (signal.aborted) return;
          this.checkedAt = Date.now();
          if (!dates.length) {
            this.text('[data-calendar-message]', 'We deliver here, but no upcoming dates are available to preview. Check your delivery options in the cart.');
            this.status.textContent = 'We deliver to ' + postcode + ', but no dates are currently available to preview.';
          } else {
            this.text('[data-next-date]', pretty(dates[0]));
            this.querySelector('[data-date-card]').hidden = false;
            this.text('[data-calendar-message]', '');
            const others = this.querySelector('[data-other-dates]'); others.replaceChildren();
            dates.slice(1).forEach(date => {const item = document.createElement('li'); item.textContent = pretty(date); others.append(item);});
            this.querySelector('[data-more-dates]').hidden = dates.length < 2;
            const cutoff = this.dataset.showCutoff === 'true' && !context.hasItems ? usualCutoff(dates[0], location) : null;
            if (cutoff && (location.timezone || calendar.timezone) === 'Australia/Melbourne') {
              this.text('[data-cutoff]', 'Usual order cutoff: ' + pretty(cutoff) + ', 11:59 pm Melbourne time.');
              this.querySelector('[data-cutoff]').hidden = false;
            }
            this.status.textContent = 'We deliver to ' + postcode + '. Next available delivery: ' + pretty(dates[0]) + '.';
          }
        } catch (error) {
          if (signal.aborted) return;
          this.text('[data-calendar-message]', 'We deliver here, but we couldn’t load the dates just now. Please try again or check your delivery options in the cart.');
          this.status.textContent = 'We deliver to ' + postcode + '. Delivery dates could not be loaded.';
        }
      } catch (error) {
        if (signal.aborted) return;
        this.status.textContent = 'We couldn’t check delivery just now. Please try again in a moment. You can also check delivery in your cart.';
        if (window.Shopify?.designMode) this.status.textContent += ' Theme editor: confirm a published, in-stock preview product is selected.';
      } finally {
        if (this.request === request) {
          this.button.disabled = false;
          this.button.textContent = this.submitLabel;
          this.form.removeAttribute('aria-busy');
        }
      }
    }
  }
  customElements.define('made-delivery-finder', DeliveryFinder);
})();
